<template>
  <el-dialog
    :model-value="modelValue"
    custom-class="el-dialog--custom"
    width="70%"
    top="0"
    :title="$t('import_history.title')"
    :close-on-click-modal="true"
    :destroy-on-close="true"
    :show-close="true"
    @close="$emit('update:modelValue', false)"
  >
    <div v-if="history" class="flex flex-col">
      <span class="font-medium h-11 flex items-center">
        {{ $t('import_history.file_name') }}:
        <div v-if="history.fileUrl" class="ml-1 underline">
          {{ history.fileName }}
        </div>
        <span v-else class="ml-1">{{ history.fileName }}</span>
      </span>
      <el-table
        :data="[history]"
        class="rounded-xl w-full"
        header-cell-class-name="custom-table__header-cell"
        cell-class-name="custom-table__cell"
        border
        :empty-text="$t('empty')"
      >
        <el-table-column :label="$t('import_history.status')" :min-width="20">
          <template v-slot="scope">{{ $t(`import_history.status_${scope.row.status}`) }}</template>
        </el-table-column>
        <el-table-column prop="userFullName" :label="$t('import_history.imported_by')" :min-width="20" />
      </el-table>
      <h4 v-if="history.errorMessage" class="text-sm text-red-500 items-center flex py-2 whitespace-pre-line">
        {{ history.errorMessage }}
      </h4>

      <span class="font-medium h-11 flex items-center mt-4">
        {{ $t('import_history.details') }}
      </span>
      <el-table
        :data="history.details"
        class="table-content rounded-xl w-full"
        header-cell-class-name="custom-table__header-cell"
        cell-class-name="custom-table__cell"
        border
        :empty-text="$t('empty')"
        max-height="380px"
      >
        <el-table-column prop="module" :label="$t('import_history.module')" :min-width="30" />
        <el-table-column :label="$t('import_history.created_ids')" :min-width="15">
          <template v-slot="scope">
            <span :title="scope.row.createdIds">{{ countIds(scope.row.createdIds) }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('import_history.updated_ids')" :min-width="15">
          <template v-slot="scope">
            <span :title="scope.row.updatedIds">{{ countIds(scope.row.updatedIds) }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('import_history.deleted_ids')" :min-width="15">
          <template v-slot="scope">
            <span :title="scope.row.deletedIds">{{ countIds(scope.row.deletedIds) }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('import_history.error_info')" :min-width="60">
          <template v-slot="scope">
            <div v-if="errorLinesById[scope.row.id]?.length" class="text-red-500">
              <div v-for="(line, idx) in visibleErrorLines(scope.row.id)" :key="idx" class="break-normal">
                {{ line }}
              </div>
              <el-button
                v-if="errorLinesById[scope.row.id].length > ERROR_PREVIEW_LINES"
                type="text"
                size="mini"
                class="p-0"
                @click="toggleErrorInfo(scope.row.id)"
              >
                {{
                  expandedErrorIds.includes(scope.row.id)
                    ? $t('import_history.see_less')
                    : $t('import_history.see_more', {
                        count: errorLinesById[scope.row.id].length - ERROR_PREVIEW_LINES,
                      })
                }}
              </el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>
    </div>
  </el-dialog>
</template>

<script lang="ts">
import { IImportHistory } from '@/apis/masterDataAPI'
import { TIMETABLE_DATETIME } from '@/utils/constants'
import { formatDate } from '@/utils/timeUtils'
import { Options, Vue } from 'vue-class-component'
import { Prop, Watch } from 'vue-property-decorator'

@Options({
  name: 'ImportHistoryDialog',
  emits: ['update:modelValue'],
})
export default class ImportHistoryDialog extends Vue {
  @Prop({ type: Boolean, default: false }) readonly modelValue!: boolean
  @Prop({ type: Object, default: null }) readonly history!: IImportHistory | null

  readonly ERROR_PREVIEW_LINES = 2
  expandedErrorIds: number[] = []

  @Watch('modelValue')
  onVisibilityChange(val: boolean) {
    if (!val) this.expandedErrorIds = []
  }

  get errorLinesById(): Record<number, string[]> {
    const map: Record<number, string[]> = {}
    for (const detail of this.history?.details ?? []) {
      map[detail.id] = this.parseErrorInfo(detail.errorInfo)
    }
    return map
  }

  visibleErrorLines(id: number): string[] {
    const lines = this.errorLinesById[id] ?? []
    return this.expandedErrorIds.includes(id) ? lines : lines.slice(0, this.ERROR_PREVIEW_LINES)
  }

  toggleErrorInfo(id: number) {
    this.expandedErrorIds = this.expandedErrorIds.includes(id)
      ? this.expandedErrorIds.filter((i) => i !== id)
      : [...this.expandedErrorIds, id]
  }

  formatTime(dateStr?: string): string {
    return dateStr ? formatDate(dateStr, TIMETABLE_DATETIME.DATETIME_WITH_SECOND_FORMAT) : ''
  }

  countIds(ids?: string): number {
    return ids ? ids.split(',').filter(Boolean).length : 0
  }

  parseErrorInfo(errorInfo?: string): string[] {
    if (!errorInfo) return []
    try {
      const parsed = JSON.parse(errorInfo)
      if (Array.isArray(parsed)) {
        return parsed.map((e) =>
          typeof e === 'object' && e !== null
            ? Object.entries(e)
                .map(([k, v]) => `${k}: ${v}`)
                .join(', ')
            : String(e)
        )
      }
    } catch (e) {}
    return [errorInfo]
  }
}
</script>
